import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Pencil,
  MoreHorizontal,
  Tag,
  ClipboardList,
  FileText,
  ScanLine,
  Mail,
  Loader2,
} from 'lucide-react';
import { isPlannerOrAbove } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useViewMode } from '../context/ViewModeContext';
import { useEvent, useEventOverview, useUpdateEvent } from '../hooks/useEvents';
import { useExpenseItems } from '../hooks/useExpenses';
import { useEventClientLedger } from '../hooks/useClientPayments';
import { useThreadCounts } from '../hooks/useThreadCounts';
import { useRecentEvents } from '../hooks/useRecentEvents';
import { EntityModal } from '../components/common/EntityModal';
import { DropdownMenu } from '../components/common/DropdownMenu';
import { TabNav } from '../components/common/TabNav';
import {
  EventDetailPanel,
  EventLogo,
  EventTagsModal,
  EventSummaryPrintModal,
} from '../components/events';
import { EventHeader } from '../components/events/EventHeader';
import { EventDateChip, EventVenueChip } from '../components/events/workspace/EventHeaderEditors';
import { EventStatusMenu } from '../components/events/EventStatusBadge';
import { EventWorkspaceContext } from '../components/events/workspace/EventWorkspaceContext';
import {
  EVENT_OVERFLOW_TABS,
  EVENT_TABS,
  activeEventTab,
  eventTabPath,
} from '../components/events/workspace/eventTabs';
import { formatMoney } from '../components/events/workspace/EventOverviewTab';

type Dialog = 'edit' | 'tags' | 'summary-pdf' | 'itemized-pdf' | null;

/**
 * The producer's event workspace: a pinned header (logo, name, countdown,
 * status you can change in place, date, venue, balance, Edit and a "More"
 * menu) over a row of tabs, each at its own address under `/events/:id`.
 *
 * Clients reach some of the same addresses (the timeline, the task lists,
 * the guest list); they get the page alone, without the producer's header.
 */
export function EventWorkspacePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const isProducer = !!user && isPlannerOrAbove(user.systemRole);

  if (!isProducer || !eventId) return <Outlet />;
  return <ProducerWorkspace eventId={eventId} />;
}

function ProducerWorkspace({ eventId }: { eventId: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { isMobile } = useViewMode();
  const { data: event, isLoading, error } = useEvent(eventId);
  const updateEvent = useUpdateEvent();
  const { record: recordRecent, forget: forgetRecent } = useRecentEvents();

  // Remember it for the sidebar's "Recent" list and the quick search; the
  // name is re-recorded after a rename so the list never shows the old one.
  // An event the server says is gone (or no longer ours) is dropped from it;
  // a failed request is not, since the event may open fine next time.
  const eventName = event?.name;
  const gone = [403, 404].includes(
    (error as { response?: { status?: number } } | null)?.response?.status ?? 0,
  );
  useEffect(() => {
    // The error first: a refetch that is refused keeps the cached name.
    if (gone) forgetRecent(eventId);
    else if (eventName) recordRecent({ id: eventId, name: eventName });
  }, [eventId, eventName, gone, recordRecent, forgetRecent]);
  const { data: overview } = useEventOverview(eventId);
  const { countFor } = useThreadCounts('Event', [eventId]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const printing = dialog === 'summary-pdf' || dialog === 'itemized-pdf';
  const { data: expenseItems = [] } = useExpenseItems({ eventId });
  const { data: ledger } = useEventClientLedger(printing ? eventId : undefined);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Event not found</p>
        <button
          onClick={() => navigate('/events')}
          className="mt-4 text-primary-600 hover:text-primary-700 text-sm"
        >
          Back to Events
        </button>
      </div>
    );
  }

  const activeTab = activeEventTab(pathname, eventId);
  const openTasks = overview ? overview.tasks.total - overview.tasks.done : null;
  const tabs = EVENT_TABS.map((t) => ({
    id: t.id,
    label: t.label,
    badge:
      t.id === 'tasks'
        ? openTasks
        : t.id === 'conversations'
          ? (countFor(eventId)?.total ?? null)
          : null,
  }));
  const balanceDue = overview?.money.clientBalanceDue ?? 0;

  return (
    <div>
      <EventHeader
        compact={isMobile}
        backTo={{ label: 'Events', to: '/events' }}
        title={event.name}
        onRename={(name) => updateEvent.mutateAsync({ id: event.id, updates: { name } })}
        startAt={event.startAt}
        logo={
          <EventLogo
            eventId={event.id}
            eventLogoUrl={event.logoUrl}
            editable
            sizeClassName={isMobile ? 'w-12 h-12' : 'w-16 h-16'}
          />
        }
        meta={
          <>
            <EventStatusMenu eventId={event.id} status={event.status} />
            <EventDateChip event={event} />
            <EventVenueChip event={event} />
            {event.eventTypeName && (
              <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold">
                {event.eventTypeName}
              </span>
            )}
            {balanceDue > 0.005 && (
              <button
                type="button"
                onClick={() => navigate(eventTabPath(eventId, 'money'))}
                className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold hover:bg-red-200"
                title="What the client still has to provide"
              >
                Balance due {formatMoney(balanceDue)}
              </button>
            )}
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => setDialog('edit')}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            >
              <Pencil size={15} />
              Edit
            </button>
            <DropdownMenu
              align="end"
              ariaLabel="More actions"
              items={[
                {
                  id: 'tags',
                  label: 'Tags',
                  icon: <Tag size={15} />,
                  onSelect: () => setDialog('tags'),
                },
                {
                  id: 'itemized-list',
                  label: 'Items',
                  icon: <ClipboardList size={15} />,
                  onSelect: () => navigate(`${eventTabPath(eventId, 'vendors')}?view=items`),
                },
                {
                  id: 'summary-pdf',
                  label: 'Summary PDF',
                  icon: <FileText size={15} />,
                  dividerBefore: true,
                  onSelect: () => setDialog('summary-pdf'),
                },
                {
                  id: 'itemized-pdf',
                  label: 'Itemized PDF',
                  icon: <FileText size={15} />,
                  onSelect: () => setDialog('itemized-pdf'),
                },
                {
                  id: 'invitations',
                  label: 'Portal invitations',
                  icon: <Mail size={15} />,
                  dividerBefore: true,
                  onSelect: () => navigate(`/events/${eventId}/invitations`),
                },
                {
                  id: 'checkin',
                  label: 'Check-in mode',
                  icon: <ScanLine size={15} />,
                  onSelect: () => navigate(`/events/${eventId}/checkin`),
                },
              ]}
              trigger={(props) => (
                <button
                  type="button"
                  {...props}
                  aria-label="More actions"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                >
                  <MoreHorizontal size={18} />
                </button>
              )}
            />
          </>
        }
        tabs={
          <TabNav
            ariaLabel="Event sections"
            items={tabs}
            overflowItems={EVENT_OVERFLOW_TABS.map((t) => ({ id: t.id, label: t.label }))}
            activeId={activeTab}
            onSelect={(id) => navigate(eventTabPath(eventId, id))}
          />
        }
      />

      <EventWorkspaceContext.Provider value>
        <Outlet />
      </EventWorkspaceContext.Provider>

      {user && (
        <EntityModal
          open={dialog === 'edit'}
          onClose={() => setDialog(null)}
          title={`Edit: ${event.name}`}
          size="lg"
        >
          <EventDetailPanel
            eventId={eventId}
            onClose={() => setDialog(null)}
            onUnsavedChange={() => {}}
            currentUser={user}
          />
        </EntityModal>
      )}
      {dialog === 'tags' && <EventTagsModal eventId={eventId} onClose={() => setDialog(null)} />}
      {printing && (
        <EventSummaryPrintModal
          event={event as never}
          expenses={expenseItems}
          ledger={ledger}
          groupBy={dialog === 'itemized-pdf' ? 'item' : undefined}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
